import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe, Edit2, Save, X, ExternalLink } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface Props {
  clientId: number;
}

export default function SitemapTab({ clientId }: Props) {
  const [client, setClient] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sitemapUrl, setSitemapUrl] = useState('');

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/clients/${clientId}`);
      setClient(response.data);
      setSitemapUrl(response.data.sitemap_url || '');
    } catch (error) {
      console.error('Failed to load client:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_BASE_URL}/clients/${clientId}`, { sitemap_url: sitemapUrl });
      await loadData();
      setEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save sitemap URL');
    } finally {
      setSaving(false);
    }
  };

  if (!client) return <div className="text-center py-12 text-gray-500">Loading...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-600" />
          Sitemap
        </h2>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => { setEditing(false); loadData(); }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <X className="w-4 h-4 inline mr-1" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 inline mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <p className="text-sm text-gray-600 mb-4">
          The sitemap helps us understand your site structure for content planning and SEO optimization.
        </p>

        {editing ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sitemap URL</label>
            <input
              type="url"
              value={sitemapUrl}
              onChange={(e) => setSitemapUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com/sitemap.xml"
            />
            <p className="text-xs text-gray-500 mt-2">
              Common sitemap locations: /sitemap.xml, /sitemap_index.xml, /sitemap/
            </p>
          </div>
        ) : (
          <div>
            {client.sitemap_url ? (
              <div className="flex items-center gap-3">
                <a
                  href={client.sitemap_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-2 text-lg"
                >
                  {client.sitemap_url}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            ) : (
              <div className="text-center py-12">
                <Globe className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No sitemap URL configured</p>
                <button
                  onClick={() => setEditing(true)}
                  className="mt-4 text-blue-600 hover:underline text-sm"
                >
                  Add sitemap URL
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}